import { useState, useRef, useEffect } from 'react';
import { Bars3Icon } from '@heroicons/react/24/outline';

/**
 * MobileIconSelector Component
 * 
 * A mobile-friendly icon-only selector with:
 * - Title and current selection always visible
 * - Horizontal swipeable scroll for other options (icon-only)
 * - Dot indicators for navigation
 * - Hamburger menu option for quick access
 * 
 * @param {Array} options - Array of option objects: { value, label, icon, count }
 * @param {string} currentValue - Currently selected option value
 * @param {Function} onChange - Callback when option changes
 * @param {string} title - Title for the selector
 */
const MobileIconSelector = ({ 
  options = [], 
  currentValue, 
  onChange, 
  title = "Options"
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const scrollContainerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isScrolling = useRef(false);

  // Find current option index
  const currentIndex = options.findIndex(o => o.value === currentValue);
  const currentOptionData = options[currentIndex] || options[0];

  // Calculate scroll indicators - recalculate when menu state changes
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const scrollWidth = scrollContainerRef.current.scrollWidth || 0;
      const clientWidth = scrollContainerRef.current.clientWidth || 0;
      setCanScroll(scrollWidth > clientWidth);
    }
  };

  // Handle scroll position update
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
      updateScrollState();
    }
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isScrolling.current = false;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current || !touchStartY.current) return;
    
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const diffX = touchStartX.current - touchX;
    const diffY = touchStartY.current - touchY;

    // Determine if this is a horizontal scroll
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      isScrolling.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isScrolling.current) {
      // If not scrolling, treat as tap
      const touchX = e.changedTouches[0].clientX;
      const touchY = e.changedTouches[0].clientY;
      const element = document.elementFromPoint(touchX, touchY);
      const button = element?.closest('button');
      if (button && button.dataset.value) {
        onChange(button.dataset.value);
      }
    }
    touchStartX.current = 0;
    touchStartY.current = 0;
    isScrolling.current = false;
  };

  // Recalculate scroll state when menu closes, component mounts, or options change
  useEffect(() => {
    if (!showMenu) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          updateScrollState();
        }, 150);
      });
    }
  }, [showMenu, options.length]);

  // Also recalculate on mount
  useEffect(() => {
    updateScrollState();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (showMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.mobile-icon-selector')) {
          setShowMenu(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenu]);

  return (
    <div className="mobile-icon-selector bg-white rounded-xl p-2 sm:p-3 md:p-4 mb-4 sm:mb-6 shadow-sm border border-gray-200 max-w-full md:max-w-full mx-auto">
      {/* Header: Title, Current Selection, Hamburger */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-700 whitespace-nowrap">{title}</h3>
          <span className="text-gray-400 text-xs">•</span>
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {currentOptionData?.icon && (
              <span className="flex-shrink-0" style={{ width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ transform: 'scale(0.7)', display: 'inline-block' }}>
                  {currentOptionData.icon}
                </span>
              </span>
            )}
            <span className="text-xs font-medium text-gray-900 truncate">
              {currentOptionData?.label || 'Select Option'}
            </span>
            {currentOptionData?.count !== undefined && currentOptionData.count > 0 && (
              <span className="text-xs text-gray-500 ml-1">
                {currentOptionData.count}
              </span>
            )}
          </div>
        </div>
        
          {/* Hamburger Menu Button - Mobile Only */}
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="md:hidden rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 flex items-center justify-center"
            style={{ padding: '4.8px' }}
            aria-label="Toggle menu"
          >
            <Bars3Icon className="h-4 w-4 text-gray-600" />
          </button>
      </div>

      {/* Hamburger Menu Dropdown - Mobile Only */}
      {showMenu && (
        <div className="md:hidden mb-2.5 border-t border-gray-200 pt-2.5">
          <div className="grid grid-cols-3 gap-2">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setShowMenu(false);
                }}
                className={`flex flex-col items-center justify-center gap-1 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                  currentValue === option.value
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={option.label}
              >
                {option.icon && (
                  <span className="flex-shrink-0" style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {option.icon}
                  </span>
                )}
                {option.count !== undefined && option.count > 0 && (
                  <span className={`text-xs ${
                    currentValue === option.value ? 'text-white' : 'text-gray-500'
                  }`}>
                    {option.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Horizontal Scrollable Icon Options - Hide when hamburger menu is open */}
      {!showMenu && (
        <div className="relative">
          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="flex gap-1.5 sm:gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible scrollbar-hide pb-2 -mx-1 px-1 sm:justify-center md:justify-start"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                data-value={option.value}
                onClick={() => onChange(option.value)}
                className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-2.5 rounded-lg text-xs sm:text-sm md:text-base font-medium transition-all whitespace-nowrap flex-1 sm:flex-initial md:flex-initial min-w-[50px] sm:min-w-[70px] md:min-w-auto ${
                  currentValue === option.value
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={option.label}
              >
                {option.icon && (
                  <span className="flex-shrink-0 md:w-5 md:h-5" style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="sm:scale-110 md:scale-100" style={{ transform: 'scale(1)', display: 'inline-block' }}>
                      {option.icon}
                    </span>
                  </span>
                )}
                {/* Show label text on desktop (md and up) */}
                <span className="hidden md:inline text-base font-medium">
                  {option.label}
                </span>
                {option.count !== undefined && option.count > 0 && (
                  <span className={`text-xs sm:text-sm md:text-base ${
                    currentValue === option.value ? 'text-white' : 'text-gray-500'
                  }`}>
                    {option.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Scroll Indicators (Dots) - Only show if scrollable */}
          {canScroll && (
            <div className="flex justify-center gap-0.5 mt-1">
              {options.map((_, index) => {
                const isActive = index === currentIndex;
                const isVisible = (() => {
                  if (!scrollContainerRef.current) return false;
                  const itemWidth = 70; // Approximate button width
                  const visibleStart = scrollPosition / itemWidth;
                  const visibleEnd = (scrollPosition + scrollContainerRef.current.clientWidth) / itemWidth;
                  return index >= Math.floor(visibleStart) && index <= Math.ceil(visibleEnd);
                })();

                return (
                  <button
                    key={index}
                    onClick={() => {
                      const button = scrollContainerRef.current?.querySelector(
                        `[data-value="${options[index].value}"]`
                      );
                      if (button) {
                        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                      }
                    }}
                    className={`rounded-full transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 shadow-sm shadow-indigo-300/50'
                        : isVisible
                        ? 'bg-gray-400 shadow-sm'
                        : 'bg-gray-300'
                    }`}
                    style={{
                      ...(isActive && {
                        boxShadow: '0 0.5px 1px rgba(99, 102, 241, 0.4), inset 0 0.5px 0 rgba(255, 255, 255, 0.3)',
                        width: '15px',
                        height: '15px',
                        minWidth: '15px',
                        minHeight: '15px',
                      }),
                      ...(!isActive && {
                        width: '10px',
                        height: '10px',
                        minWidth: '10px',
                        minHeight: '10px',
                      }),
                    }}
                    aria-label={`Go to ${options[index].label}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileIconSelector;

